import { BRAND, HEADER_CSS, esc, page, richText, traceLine } from './base.js'

/**
 * ใบส่งตัว (referral / referral_en) and ใบส่งเอ็กซเรย์ (xray_referral) — HTML
 * replacements for the Google Docs templates. Both come from the same dialog
 * (ReferralDialog) and payload (buildReferralPayload), so they share this
 * module; only the lower half differs:
 *   referral — a blank reply section + signature for the receiving clinician.
 *   xray     — the radiograph request checklist the radiology unit ticks.
 *
 * `refer_detail` is the editor's rich text, printed through richText() — an
 * allowlist, because on the server it is just whatever the request sent.
 *
 * Wording follows the Google Docs templates. The X-ray tooth charts there had
 * a typo (primary row "E D C B A | 1 2 … 8"); here they are A–E on both sides.
 */

const L = {
  th: {
    ref: 'เลขที่อ้างอิง', date: 'วันที่รับบริการ',
    patient: 'ข้อมูลผู้ป่วย', hn: 'HN', age: 'อายุ', years: 'ปี', doctor: 'ทันตแพทย์ผู้ส่งตัว', lic: 'ท.',
    issuer: 'สถานพยาบาลผู้ส่งตัว', clinicName: 'คลินิกทันตกรรมสไมล์ซีซันส์ สาขา', branchLic: 'ใบอนุญาตประกอบกิจการสถานพยาบาล', sso: 'รหัสสถานพยาบาล',
    clinic: 'คลินิกทันตกรรมสไมล์ซีซันส์ สาขา',
    sign: 'ทันตแพทย์ผู้รักษา',
  },
  en: {
    ref: 'Reference no.', date: 'Date of service',
    patient: 'Patient', hn: 'HN', age: 'Age', years: 'years', doctor: 'Referring dentist', lic: 'License',
    issuer: 'Referring facility', clinicName: 'Smile Seasons Dental Clinic · ', branchLic: 'Healthcare facility license', sso: 'Facility code',
    clinic: 'Smile Seasons Dental Clinic · ',
    sign: 'Referring dentist',
  },
}

const KINDS = {
  referral: {
    th: {
      title: 'ใบส่งตัว', sub: 'Referral Document',
      to: 'เรียน ทันตแพทย์ / อาจารย์ทันตแพทย์ แพทย์ / อาจารย์แพทย์ ที่เคารพ',
      detail: 'รายละเอียดประเด็นส่งตัว / ปรึกษา',
      reply: 'รายละเอียดการรักษา / ให้คำปรึกษา', replySign: 'ทันตแพทย์ / แพทย์ผู้ให้การรักษาหรือให้คำปรึกษา',
    },
    en: {
      title: 'Patient Referral', sub: 'ใบส่งตัว',
      to: 'To the Receiving Clinician,',
      detail: 'Intraoral Examination / Diagnosis / Referral Note',
      reply: 'Opinion of Dentist or Attending Physician', replySign: 'Consulted dentist / physician',
    },
  },
  xray: {
    th: {
      title: 'ใบส่งเอ็กซเรย์', sub: 'ใบส่งตัวผู้ป่วยเพื่อรับการตรวจทางรังสีวิทยา · Radiographic Referral',
      to: 'เรียน ทันตแพทย์ แพทย์ เจ้าหน้าที่ผู้เกี่ยวข้อง',
      detail: 'ประวัติและการตรวจอย่างย่อ',
      replySign: 'Consulted Dentist / Physician',
    },
  },
}

export function renderReferral(d, { lang = 'th', assets = {}, kind = 'referral' } = {}) {
  const en = lang === 'en'
  const k = KINDS[kind] || KINDS.referral
  const t = { ...(L[lang] || L.th), ...(k[lang] || k.th) }
  const pick = (th, enKey) => (en && d[enKey] != null && d[enKey] !== '' ? d[enKey] : d[th])

  const branch = pick('branch_name', 'branch_nameEN')
  const doctorTh = `${d.doctor_prefix || ''}${d.doctor_fullname || ''}`.trim()
  const doctor = en ? (d.doctor_fullnameEN || doctorTh) : doctorTh
  const patient = en
    ? (d.fullnameEN || d.patient_fullname || d.fullname)
    : `${d.patient_prefix || d.paitent_prefix || ''}${d.patient_fullname || d.fullname || ''}`
  const date = en ? (d.date || d.date_thai) : (d.date_thai || d.date)
  const lic = d.doctor_license ? `${t.lic} ${esc(d.doctor_license)}` : ''
  const B = (v) => `<b>${esc(v)}</b>`
  const N = (v) => `<b class="num">${esc(v)}</b>`

  const age = d.patient_age
  const statement = kind === 'xray'
    ? `ขอส่งตัวผู้ป่วยชื่อ ${B(patient)}${age ? ` อายุ ${N(age)} ปี` : ''} รหัสผู้ป่วย (HN) ${N(d.HN)} เพื่อรับการตรวจรักษาทางรังสีวิทยา โดยมีประวัติและการตรวจอย่างย่อดังนี้`
    : en
      ? `I, ${B(doctor)}, DDS${d.doctor_license ? `, license no. ${N(d.doctor_license)}` : ''}, am referring patient ${B(patient)}${age ? `, aged ${N(age)} years` : ''}, patient ID (HN) ${N(d.HN)}, for further investigation, treatment, or consultation. Details are as follows:`
      : `ข้าพเจ้า ${B(doctorTh)}${d.doctor_license ? ` ใบอนุญาตประกอบวิชาชีพทันตกรรมหมายเลข ${N(`ท. ${d.doctor_license}`)}` : ''} ขอส่งตัวผู้ป่วยชื่อ ${B(patient)}${age ? ` อายุ ${N(age)} ปี` : ''} รหัสผู้ป่วย (HN) ${N(d.HN)} เพื่อรับการรักษา หรือปรึกษา โดยมีรายละเอียดดังนี้`

  const detail = String(d.refer_detail ?? '').trim()
  const signBox = (name, role, blank = false) => `
    <div class="sign-box">
      <div class="sign-line"></div>
      <b>(${blank ? '<span class="blank"></span>' : esc(name)})</b>
      <span>${role}</span>
    </div>`

  const lower = kind === 'xray'
    ? xrayRequest(t, `${esc(patient)} · HN ${esc(d.HN)}${d.ref_num ? ` · ${t.ref} <span class="num">${esc(d.ref_num)}</span>` : ''}`)
    : `
  <section class="block reply">
    <div class="block-label">${t.reply}</div>
    <div class="lines">${'<i></i>'.repeat(4)}</div>
  </section>
  <section class="sign">${signBox('', t.replySign, true)}</section>`

  const body = `
  <header class="top">
    <div>
      <h1>${t.title}</h1>
      <div class="sub">${t.sub}</div>
    </div>
    ${assets.logo ? `<img class="logo" src="${esc(assets.logo)}" alt="Smile Seasons Dental Clinic">` : ''}
  </header>

  <section class="meta">
    <div><span>${t.ref}</span><b class="num">${esc(d.ref_num || '-')}</b></div>
    <div><span>${t.date}</span><b>${esc(date || '-')}</b></div>
  </section>

  <section class="parties">
    <div class="party">
      <div class="label">${t.patient}</div>
      <div class="name">${esc(patient)}</div>
      <div class="kv"><span>${t.hn}</span>${esc(d.HN)}</div>
      ${age ? `<div class="kv"><span>${t.age}</span>${esc(age)} ${t.years}</div>` : ''}
      <div class="kv"><span>${t.doctor}</span>${esc(doctor)}${lic ? ` <span class="muted">(${lic})</span>` : ''}</div>
    </div>
    <div class="party">
      <div class="label">${t.issuer}</div>
      <div class="name">${esc(t.clinicName)}${esc(branch || '')}</div>
      ${d.branch_license_number ? `<div class="kv"><span>${t.branchLic}</span><span class="num">${esc(d.branch_license_number)}</span></div>` : ''}
      ${d.branch_ssonumber ? `<div class="kv"><span>${t.sso}</span><span class="num">${esc(d.branch_ssonumber)}</span></div>` : ''}
    </div>
  </section>

  <p class="to">${t.to}</p>
  <p class="statement">${statement}</p>

  <section class="block">
    <div class="block-label">${t.detail}</div>
    <div class="block-body rich">${detail ? richText(detail) : '<span class="muted">-</span>'}</div>
  </section>

  <section class="sign">${signBox(doctor, `${t.sign}${lic ? ` · ${lic}` : ''}`)}</section>
  ${lower}`

  return page({
    title: `${t.title} ${d.ref_num || d.HN || ''}`.trim(),
    lang,
    assets,
    footer: `${t.clinic}${branch || ''}`,
    meta: traceLine(d, lang),
    body,
    css: CSS,
  })
}

// ── The X-ray request checklist (filled by hand at the radiology unit) ──────

const box = (label) => `<span class="cb"><i></i>${label}</span>`

// One FDI-style quadrant grid: upper row and lower row, patient's right on the left.
function toothChart(primary) {
  const right = primary ? ['E', 'D', 'C', 'B', 'A'] : ['8', '7', '6', '5', '4', '3', '2', '1']
  const left = [...right].reverse()
  const row = () => `<tr>${right.map((n) => `<td>${n}</td>`).join('')}<td class="mid"></td>${left.map((n) => `<td>${n}</td>`).join('')}</tr>`
  return `<table class="teeth${primary ? ' primary' : ''}">${row()}${row()}</table>`
}

function occlusal(label) {
  const arch = (name) => `<div class="row arch"><span>${name}</span>${['Rt.', 'Ant.', 'Lt.'].map(box).join('')}</div>`
  return `<div class="row">${box(`<b>${label}</b>`)}</div>${arch('Upper')}${arch('Lower')}`
}

function xrayRequest(t, who) {
  return `
  <section class="xr">
    <div class="xr-who">${who}</div>
    <div class="block-label">Intra-Oral Examination</div>
    <div class="xr-grid">
      <div class="xr-item">
        <h3>Full-mouth Periapical</h3>
        <div class="row">${box('Bisecting')}${box('Paralleling')}</div>
        <h3>Occlusal Radiograph</h3>
        ${occlusal('Topographic')}
        ${occlusal('Cross-sectional')}
      </div>
      <div class="xr-item">
        <h3>Periapical</h3>
        <div class="row">${box('Bisecting')}${box('Paralleling')}</div>
        <div class="charts">${toothChart(false)}${toothChart(true)}</div>
      </div>
      <div class="xr-item">
        <h3>Bite-wing Radiograph</h3>
        <div class="row">${box('Horizontal')}${box('Vertical')}</div>
      </div>
      <div class="xr-item">
        <div class="charts">${toothChart(false)}${toothChart(true)}</div>
      </div>
    </div>

    <div class="block-label">Extra-Oral Examination</div>
    <div class="extra">
      ${['Panoramic Radiograph', 'Lateral Cephalogram', 'PA Cephalogram', 'Hand-Wrist Radiograph',
        'PA Mandible Radiograph', 'Transcranial Radiograph', 'Towne Radiograph', 'Reverse Towne Radiograph',
        'Submentovertex Radiograph', 'Waters Radiograph'].map(box).join('')}
      <span class="cb cbct"><i></i>Cone Beam CT · Area <span class="fill"></span></span>
    </div>

    <section class="sign">
      <div class="sign-box">
        <div class="sign-line"></div>
        <b>(<span class="blank"></span>)</b>
        <span>${t.replySign}</span>
      </div>
    </section>
  </section>`
}

const CSS = `${HEADER_CSS}
.to { margin: 0 0 2mm; font-weight: 600; }
.statement { margin: 0 0 5mm; font-size: 10.5pt; line-height: 1.75; text-indent: 8mm; }
.statement b { font-weight: 600; }

.block { margin-bottom: 4mm; break-inside: avoid; }
.block-label { padding: 1.8mm 3mm; background: ${BRAND.blueSoft}; border-left: 2px solid ${BRAND.blue}; font-size: 9pt; font-weight: 600; color: ${BRAND.blue}; }
.block-body { padding: 2.4mm 3mm 0; min-height: 10mm; overflow-wrap: anywhere; }
.rich p { margin: 0 0 1.5mm; }
.rich ul, .rich ol { margin: 0 0 1.5mm; padding-left: 6mm; }
.rich b { font-weight: 600; }

.sign { display: flex; justify-content: flex-end; margin: 4mm 0 4mm; break-inside: avoid; }
.sign-box { width: 76mm; display: flex; flex-direction: column; align-items: center; text-align: center; font-size: 9.5pt; }
.sign-line { width: 100%; height: 12mm; border-bottom: 1px dotted #94a3b8; margin-bottom: 2mm; }
.sign-box b { font-weight: 600; font-size: 10.5pt; }
.sign-box span { color: ${BRAND.muted}; }
.blank { display: inline-block; width: 55mm; }

.reply { break-inside: avoid; }
.lines { padding: 0 3mm; }
.lines i { display: block; height: 7.5mm; border-bottom: 1px solid ${BRAND.rule}; }

/* X-ray request */
/* Its own page: the radiology unit fills it in, and it never splits mid-form. */
.xr { break-before: page; font-size: 9pt; }
.xr-who { margin-bottom: 3mm; padding-bottom: 2mm; border-bottom: 1px solid ${BRAND.rule}; font-size: 9.5pt; font-weight: 600; }   /* the page may travel alone */
.xr .block-label { margin-bottom: 3mm; }
.xr .sign { margin-top: 14mm; }
.xr-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2mm 8mm; margin-bottom: 4mm; break-inside: avoid; }
.xr h3 { margin: 1.5mm 0 1mm; font-size: 9pt; font-weight: 600; color: ${BRAND.ink}; }
.xr .row { display: flex; flex-wrap: wrap; align-items: center; gap: 1.5mm 4mm; margin-bottom: 1.5mm; }
.cb { display: inline-flex; align-items: center; gap: 1.5mm; white-space: nowrap; }
.cb > i { width: 3.2mm; height: 3.2mm; border: 1px solid #64748b; border-radius: .6mm; flex: none; }
.cb b { font-weight: 600; }
.arch { margin-left: 5.5mm; }
.arch > span:first-child { width: 11mm; color: ${BRAND.muted}; }
.charts { display: flex; flex-direction: column; align-items: center; gap: 2mm; margin-top: 1mm; }
table.teeth { border-collapse: collapse; font-variant-numeric: tabular-nums; }
table.teeth td { width: 5.2mm; height: 5mm; text-align: center; font-size: 8.5pt; color: #334155; padding: 0; }
table.teeth tr:first-child td { border-bottom: 1px solid #64748b; }
table.teeth td.mid { width: 0; border-left: 1px solid #64748b; }
.extra { display: grid; grid-template-columns: 1fr 1fr; gap: 1.8mm 6mm; margin-bottom: 2mm; break-inside: avoid; }
.extra .cbct { grid-column: 1 / -1; }
.fill { display: inline-block; width: 70mm; border-bottom: 1px dotted #94a3b8; height: 4mm; }
`
